/**
 * InvoicePreview — the operator's read of one compiled invoice.
 *
 * Route: /t/:tenantSlug/invoices/:id
 *
 * A read-only render of one invoice: client, bill-to, line items (qty /
 * rate / amount), subtotal/total, period, and doc_number once issued —
 * for review before approve/issue.
 *
 * Actions are capability-gated and server-authoritative (the server re-derives
 * money on every transition): draft -> Approve / Add unbilled charges / Discard;
 * approved -> Issue / Reopen; issued -> Void (owner-only; a void-not-delete
 * ruling — numbered documents never vanish, so an issued invoice's delete
 * affordance is void, not discard). All gating mirrors
 * api/_shared/capabilities.ts (owner-only actions; view for office roles).
 *
 * Invoice workflow pass (founder feedback day-1 live, 2026-07-10): a back
 * affordance (history back, invoices-list fallback), the client name links
 * to the client record (view_all_clinical holders), the missing bill-to
 * states link to the Pre-billing tab's Billing details card, draft lines
 * carry an "Edit at source" link (month-scoped — lines are never edited
 * in place; _invoiceDerivation stays the one writer). A draft follows its
 * notes: an edit at the source updates it in the same save. "Add unbilled
 * charges" brings in any other unbilled charge for the period and says
 * how many it added; a draft with no lines says so.
 *
 * ===================================================================
 * THE HOUSE LAYOUT PASS (kith/invoice-preview-shell)
 * ===================================================================
 * Photographed at 1440px, this page was off the layout system entirely:
 * a hand-rolled `p-6 space-y-6 max-w-3xl` div pinned ~720px of content
 * flush left with half the screen blank, no eyebrow, no page contract
 * (a back-arrow link stood in for one), hand-rolled BTN / BTN_PRIMARY
 * class strings, hand-rolled loading and error lines, and — on the
 * failed-push state — the heaviest, darkest button on the screen
 * sitting INSIDE the QuickBooks panel while the invoice's own
 * destructive action was a bare red text link below it. Every other
 * page in the app is centred and on PageShell; this one read as a
 * different product, and it is the page an operator opens ~250 times a
 * month at go-live.
 *
 * WHY PageShell's OWN HEADER AND NOT SectionBand: SectionBand is a
 * SECTION-level landmark — the forest band that names a whole area of
 * the app ("Invoices") — and this is one RECORD inside that area, so it
 * takes the shell's plain header (eyebrow + title + the header-right
 * action slot) exactly as the app's other per-record detail pages do
 * (AiToolsDraftDetail); a second forest band under the list's own would
 * announce the area twice over a single document.
 *
 * WHY `standard` (xl, 1152px): one document with a subordinate
 * integration panel is a single-column workflow, not a data cockpit —
 * `wide` is the GC-ruled grid/table axis (Directory results, the
 * Clients bands) and would stretch the invoice's own two-column facts
 * grid into a ribbon.
 *
 * THE ACTION HIERARCHY: the invoice's own actions live in the shell's
 * header-right slot — the page contract's ONE primary-action position —
 * so on every status they sit above and outside QboPushPanel, and the
 * integration's controls are panel-scoped rather than the loudest thing
 * on the page. The destructive opener stays a text chip (the standing
 * ruling: danger is the confirm dialog's submit, never the opener) but
 * is now the sanctioned DestructiveTextAction — identical geometry,
 * pepper-deep instead of pepper, which is what clears AA (5.0:1 vs
 * 3.72:1) on its own hover fill.
 */

import { type ReactNode, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BackControl } from '@/components/BackControl';
import { useTenant } from '@/lib/auth';
import {
  canViewInvoice,
  canApproveInvoice,
  canIssueInvoice,
  canCompileInvoice,
  canVoidInvoice,
  canOpenEveryClientRecord,
} from '@/lib/capabilities';
import { invoicesApi, ApiError, type InvoiceDetailWire } from '@/lib/api';
import { invalidateInvoiceReads } from '@/lib/invoiceInvalidation';
import { noBillableEventsExplanation } from '@/lib/prebilling';
import { billingHouseholdText, contactSentence, pageWriteText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { PageShell } from '@/components/primitives/PageShell';
import { DestructiveTextAction } from '@/components/workspace/TextAction';
import { invoiceStatusLabel, invoiceStatusVariant } from '@/lib/invoiceStatus';
import { displayValue } from '@/lib/valueCase';
import { VoidInvoiceDialog } from '@/components/VoidInvoiceDialog';
import { QboPushPanel, useQboPushState } from '@/components/billing/QboPushPanel';
import { InvoicePushFailedAlert } from '@/components/billing/PushFailedAlert';
import { pushFailedAlertShows } from '@/lib/qboPushStatus';

/** The document's own paper card (SectionBlock's `card` shell): on a
 *  detail page the record IS the page, so the invoice sits on paper and
 *  the QuickBooks panel keeps its quieter sage-tinted panel beneath it. */
const DOC_CARD =
  'rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-6 space-y-5';

/** Action errors keep their wire code so the render can attach the right
 *  next step (e.g. missing bill-to links to the Pre-billing tab). */
interface ActionError {
  code: string | null;
  message: string;
}

function actionErrorOf(err: unknown, contact: SupportContact): ActionError {
  // A billing household refusal (0215) carries its own sentence.
  const household = billingHouseholdText(err, contact);
  if (household !== null) return { code: (err as ApiError & { body: { error: string } }).body.error, message: household };
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const code = (err.body as { error?: string }).error ?? null;
    switch (code) {
      case 'approval_invalidated':
        return { code, message: "The work on this invoice changed after it was approved, so it wasn't issued. Press Reopen, approve it again, then issue it." };
      // RETIRED 2026-08-27 (the snapshot keeps money facts only). The copy
      // stays while the server can still return the code.
      case 'missing_billing_contact':
        return { code, message: "No one is set to receive this client's bill, so it wasn't issued. Add a billing contact on their Billing details, then issue." };
      case 'ambiguous_billing_contact':
        return { code, message: "More than one person is set as this client's main billing contact, so it wasn't issued. Keep one on their Billing details, then issue." };
      case 'illegal_transition':
        return { code, message: "That can't be done to this invoice now, so nothing changed. Reload the page to see where it stands." };
      case 'no_billable_events':
        // Explained when pre-window Ready notes caused the zero (#528
        // follow-on: approve/recompile carry the same detail as compile);
        // a genuinely empty re-derivation keeps the plain copy.
        return {
          code,
          message:
            noBillableEventsExplanation(err.body) ??
            "Nothing is left to bill for this period. If the month's notes changed, discard this draft.",
        };
      case 'reconcile_not_cleared':
        return {
          code,
          message: contactSentence(
            'No charges were added, because this month has imported hours that were never signed off. Ask {contact_billing} to sign them off, then press Add unbilled charges again.',
            contact,
          ),
        };
      default:
        return { code, message: pageWriteText(err, contact) };
    }
  }
  return { code: null, message: pageWriteText(err, contact) };
}

export function InvoicePreview(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [voidOpen, setVoidOpen] = useState(false);
  const [addResult, setAddResult] = useState<AddChargesResult | null>(null);

  const canView = canViewInvoice(whoami);
  const supportContact = useSupportContact({ enabled: false });


  const { data, isLoading, isError, error, refetch } = useQuery<InvoiceDetailWire>({
    queryKey: ['invoices', 'detail', tenantSlug, id],
    queryFn: ({ signal }) => invoicesApi.get(tenantSlug, id, signal),
    enabled: canView && id !== '',
  });

  // The invoice's QuickBooks push state, through the panel's own shared
  // hook: same queryKey and queryFn, so React Query dedupes this and the
  // panel's read to ONE fetch, and the void dialog's qboInvoiceId can
  // never disagree with the panel above it. Chosen over a second wire
  // read (duplicate fetch logic) and over queryClient.getQueryData (an
  // unsubscribed render-time pluck that misses late-arriving data).
  const pushStateQuery = useQboPushState(id, data?.invoice.status ?? '');

  const detailKey = ['invoices', 'detail', tenantSlug, id];
  const onSettled = (): void => {
    void queryClient.invalidateQueries({ queryKey: detailKey });
    // Approve, void and reopen run from THIS page and all change what is
    // unbilled, so the shared helper widens the list invalidation to the
    // sibling unbilled key the prefix match would never reach.
    invalidateInvoiceReads(queryClient, tenantSlug);
    // THE QUICKBOOKS READ DEPENDS ON THE INVOICE'S STATUS, so a status
    // change is exactly what makes it stale. Leaving it out cost the #90
    // warning the one moment it matters: an operator voids an invoice
    // that is live in QuickBooks, and because the panel had already read
    // the push state while the invoice was still ISSUED (when the server
    // correctly answers void_check null), the 30s staleTime kept serving
    // that answer. The screen said "In QuickBooks as invoice 1042" and
    // nothing else, on the one surface built to say "still open there,
    // void it in QuickBooks". Olive never voids in QuickBooks, so a
    // warning the operator has to earn by reloading is a copy left live,
    // collecting payment, on top of the one they will re-issue.
    void queryClient.invalidateQueries({ queryKey: ['qbo', 'push-state', tenantSlug, id] });
  };

  const approveMut = useMutation({
    mutationFn: () => invoicesApi.approve(tenantSlug, id),
    onMutate: () => setAddResult(null),
    onSuccess: () => { setActionError(null); onSettled(); },
    onError: (e) => setActionError(actionErrorOf(e, supportContact)),
  });
  const reopenMut = useMutation({
    mutationFn: () => invoicesApi.reopen(tenantSlug, id),
    onMutate: () => setAddResult(null),
    onSuccess: () => { setActionError(null); onSettled(); },
    onError: (e) => setActionError(actionErrorOf(e, supportContact)),
  });
  const issueMut = useMutation({
    mutationFn: () => invoicesApi.issue(tenantSlug, id),
    onMutate: () => setAddResult(null),
    onSuccess: () => { setActionError(null); onSettled(); },
    onError: (e) => setActionError(actionErrorOf(e, supportContact)),
  });
  const discardMut = useMutation({
    mutationFn: () => invoicesApi.discard(tenantSlug, id),
    onMutate: () => setAddResult(null),
    onSuccess: () => navigate(`/t/${tenantSlug}/invoices`),
    onError: (e) => setActionError(actionErrorOf(e, supportContact)),
  });
  // Draft-only: Add unbilled charges (the one shared derivation
  // server-side). Its result line stays until the next action or reload.
  const recompileMut = useMutation({
    mutationFn: () => invoicesApi.recompile(tenantSlug, id),
    onMutate: () => setAddResult(null),
    onSuccess: (res) => {
      setActionError(null);
      setAddResult({ added: res.added_count, removed: res.removed_count });
      onSettled();
    },
    onError: (e) => setActionError(actionErrorOf(e, supportContact)),
  });

  const busy =
    approveMut.isPending ||
    reopenMut.isPending ||
    issueMut.isPending ||
    discardMut.isPending ||
    recompileMut.isPending;

  // The record's own actions, laid out by the shell's action row. Built
  // here (not in the body) so that on every status the invoice's actions
  // precede and sit outside QboPushPanel: the record outranks the
  // integration.
  const actions = data ? (
    <>
      {data.invoice.status === 'draft' && canApproveInvoice(whoami) && (
        <Button data-testid="invoice-approve" disabled={busy} onClick={() => approveMut.mutate()}>
          Approve
        </Button>
      )}
      {data.invoice.status === 'draft' && canCompileInvoice(whoami) && (
        <Button
          data-testid="invoice-recompile"
          variant="secondary"
          disabled={busy}
          onClick={() => recompileMut.mutate()}
        >
          Add unbilled charges
        </Button>
      )}
      {data.invoice.status === 'draft' && canCompileInvoice(whoami) && (
        <Button
          data-testid="invoice-discard"
          variant="secondary"
          disabled={busy}
          onClick={() => discardMut.mutate()}
        >
          Discard draft
        </Button>
      )}
      {data.invoice.status === 'approved' && canIssueInvoice(whoami) && (
        <Button data-testid="invoice-issue" disabled={busy} onClick={() => issueMut.mutate()}>
          Issue
        </Button>
      )}
      {data.invoice.status === 'approved' && canApproveInvoice(whoami) && (
        <Button
          data-testid="invoice-reopen"
          variant="secondary"
          disabled={busy}
          onClick={() => reopenMut.mutate()}
        >
          Reopen
        </Button>
      )}
      {data.invoice.status === 'issued' && (
        canVoidInvoice(whoami) ? (
          // The destructive OPENER stays a text chip (danger is the
          // confirm dialog's own submit) — now the sanctioned register.
          <DestructiveTextAction
            data-testid="invoice-void-open"
            disabled={busy}
            onClick={() => setVoidOpen(true)}
          >
            Void invoice
          </DestructiveTextAction>
        ) : (
          <span data-testid="invoice-final" className="text-sm text-ink-soft">
            Issued. This invoice is final.
          </span>
        )
      )}
    </>
  ) : undefined;

  return (
    <PageShell
      eyebrow="Olive · Billing"
      title="Invoice preview"
      actions={actions}
      maxWidth="standard"
      testId="invoice-preview-page"
      access={{
        allowed: canView,
        deniedContent: (
          <div
            data-testid="invoice-preview-denied"
            className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-6 text-sm text-ink-soft"
          >
            {contactSentence(
              "Invoices aren't part of your access. Ask {contact_admin} if your work needs them.",
              supportContact,
            )}
          </div>
        ),
      }}
      async={{ what: 'this invoice',
        isLoading,
        // A settled, successful read with no body is the same dead end as
        // a failed one (the old `isError || !data` branch), and the house
        // ErrorBanner is where that is said.
        isError: isError || (!isLoading && !data),
        error,
        onRetry: () => void refetch(),
      }}
    >
      {data && (
        <InvoiceDocument
          data={data}
          tenantSlug={tenantSlug}
          canOpenRecord={canOpenEveryClientRecord(whoami)}
          actionError={actionError}
          addResult={addResult}
          pushFailedError={
            pushStateQuery.data !== undefined &&
            pushFailedAlertShows(pushStateQuery.data.status, pushStateQuery.data.qboInvoiceId, data.invoice.status)
              ? { lastError: pushStateQuery.data.attempts[pushStateQuery.data.attempts.length - 1]?.lastError ?? null }
              : null
          }
        />
      )}

      {data && voidOpen && (
        <VoidInvoiceDialog
          tenantSlug={tenantSlug}
          invoiceId={id}
          docNumber={data.invoice.doc_number}
          clientName={data.client.display_name}
          qboInvoiceId={pushStateQuery.data?.qboInvoiceId ?? null}
          onVoided={() => {
            setVoidOpen(false);
            setActionError(null);
            onSettled();
          }}
          onClose={() => setVoidOpen(false)}
        />
      )}
    </PageShell>
  );
}

/** The document itself, plus the subordinate QuickBooks panel beneath it.
 *  Split out only so the page body reads as what it is; no state of its
 *  own (every mutation stays on the page, with the action row). */
/** The preview's line about the period's case notes. */
function noteCountSentence({ total, not_ready }: { total: number; not_ready: number }): string {
  if (total === 0) return "Once case notes post for this period, you'll see them here.";
  if (not_ready === 0) {
    return total === 1 ? 'The one case note in this period is ready.' : `All ${total} case notes in this period are ready.`;
  }
  return `${not_ready} of ${total} case ${total === 1 ? 'note' : 'notes'} in this period ${not_ready === 1 ? 'is' : 'are'} still to be marked Ready.`;
}

/** What the last Add unbilled charges run did. */
interface AddChargesResult {
  added: number;
  removed: number;
}

/** The result line: how many charges were added, and, only when some
 *  were, how many were removed. */
function addChargesSentence(added: number, removed: number): string {
  const addedPart =
    added === 0
      ? 'Every charge is already on this invoice.'
      : `Added ${added} unbilled ${added === 1 ? 'charge' : 'charges'} to this invoice.`;
  if (removed === 0) return addedPart;
  const removedPart =
    removed === 1
      ? 'Removed 1 charge that no longer belongs to this invoice.'
      : `Removed ${removed} charges that no longer belong to this invoice.`;
  return `${addedPart} ${removedPart}`;
}

function InvoiceDocument({
  data,
  tenantSlug,
  canOpenRecord,
  actionError,
  addResult,
  pushFailedError,
}: {
  data: InvoiceDetailWire;
  tenantSlug: string;
  canOpenRecord: boolean;
  actionError: ActionError | null;
  addResult: AddChargesResult | null;
  /** The sync error alert (PR 4b): the latest push's class token when
   *  pushFailedAlertShows holds for this invoice, else null. */
  pushFailedError: { lastError: string | null } | null;
}): ReactNode {
  const { invoice, client, bill_to, lines, lines_source } = data;
  const noteCount = data.note_count;
  const isDraft = invoice.status === 'draft';
  const isVoid = invoice.status === 'void';
  // The client's Pre-billing tab, scoped to the invoice's month — where
  // draft lines are edited at source (hours / rate / summary live on the
  // month's Ready notes, never on the invoice).
  const preBillingHref = `/t/${tenantSlug}/clients/${client.id}?tab=pre-billing&month=${invoice.period_start.slice(0, 7)}`;

  return (
    <div data-testid="invoice-preview" className="space-y-6">
      {/* Where the person came from when the visit came from inside Olive
          (the browser's history), else the invoices list, this page's parent. */}
      <BackControl
        label="Invoices"
        ariaLabel="Back to invoices"
        to={`/t/${encodeURIComponent(tenantSlug)}/invoices`}
        testId="invoice-back"
      />

      {/* The sync error, above the document: big and red where the office
          is looking, never down in the QuickBooks panel at the foot. */}
      {pushFailedError !== null && <InvoicePushFailedAlert lastError={pushFailedError.lastError} />}

      <article className={DOC_CARD}>
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            {invoice.doc_number && (
              <div data-testid="invoice-doc-number" className="font-display text-lg text-sage-800">
                Document #{invoice.doc_number}
              </div>
            )}
            <div className="text-sm text-ink-soft">
              Period {invoice.period_start} → {invoice.period_end}
            </div>
          </div>
          <Chip variant={invoiceStatusVariant(invoice.status)} data-testid="invoice-status">
            {invoiceStatusLabel(invoice.status)}
          </Chip>
        </header>

        <section className="grid gap-4 sm:grid-cols-2 text-sm">
          <div>
            <div className="font-semibold text-ink">Client</div>
            {canOpenRecord ? (
              <Link
                data-testid="invoice-client-link"
                className="text-sage-700 underline"
                to={`/t/${tenantSlug}/clients/${client.id}`}
              >
                <span data-testid="invoice-client">{client.display_name}</span>
              </Link>
            ) : (
              <div data-testid="invoice-client">{client.display_name}</div>
            )}
            <Link
              data-testid="invoice-preview-billing-details"
              className="text-sage-700 underline"
              to={`/t/${tenantSlug}/invoices/clients/${client.id}`}
            >
              Billing details
            </Link>
          </div>
          <div>
            <div className="font-semibold text-ink">Bill to</div>
            {bill_to ? (
              <div data-testid="invoice-bill-to">
                {bill_to.contact_name}
                {bill_to.relationship ? ` (${bill_to.relationship})` : ''}
                {bill_to.address ? <div className="text-ink-soft">{bill_to.address}</div> : null}
              </div>
            ) : isVoid ? (
              /* A dead document does not need billing details set, so the
                 missing-bill-to PROMPT (and its Pre-billing link) stays off
                 a void invoice — the 2026-08-27 ruling, unchanged. What was
                 wrong was leaving the "Bill to" heading standing over
                 nothing at all: the house convention is to STATE an absence
                 (ClientRatesTab's "No rate on file"), so the absence is
                 named here in the neutral register, with no fix-it action
                 because a voided invoice needs no fix. */
              <div data-testid="invoice-bill-to-none" className="text-ink-soft">
                No billing contact was set on this invoice.
              </div>
            ) : (
              <div data-testid="invoice-bill-to-missing" className="text-pepper-deep">
                No billing contact set.{' '}
                <Link
                  data-testid="invoice-bill-to-set-link"
                  className="underline"
                  to={preBillingHref}
                >
                  Set billing details
                </Link>
              </div>
            )}
          </div>
        </section>

        {isDraft && (
          <p data-testid="invoice-draft-explainer" className="text-sm text-ink-soft">
            These lines come from the month&rsquo;s Ready notes. When someone in the office who
            may change other people&rsquo;s notes edits a note on Pre-billing, the note stays
            Ready and this draft updates at once. When a note&rsquo;s author edits it, the note
            comes off Ready, so its charge leaves this draft. The draft updates when the note is
            marked Ready again. A note marked Ready joins this draft. Add unbilled charges brings
            in any other unbilled charge for the period.
          </p>
        )}

        {isDraft && addResult !== null && (
          <p data-testid="invoice-add-charges-result" role="status" className="text-sm text-ink">
            {addChargesSentence(addResult.added, addResult.removed)}
          </p>
        )}

        {isDraft && lines.length === 0 && (
          <p data-testid="invoice-empty-draft" className="text-sm text-ink">
            Mark this draft's notes Ready on Pre-billing to fill it, or discard the draft.
          </p>
        )}

        {/* The period's case notes and how many are not Ready. The server
            sends null where the count is not served (a void invoice, or a
            seat that does not read every client), and then nothing shows. */}
        {noteCount !== null && (
          // Notes not ready are something to do: the line is bold, in the
          // house error red (pepper-deep, AA on the card). A period with
          // nothing to do keeps the quiet ink.
          <p
            data-testid="invoice-note-count"
            className={noteCount.not_ready > 0 ? 'text-sm font-semibold text-pepper-deep' : 'text-sm text-ink-soft'}
          >
            {noteCountSentence(noteCount)}
            {noteCount.not_ready > 0 && (
              <>
                {' '}
                <Link data-testid="invoice-note-count-link" className="underline" to={preBillingHref}>
                  Open Pre-billing
                </Link>
              </>
            )}
          </p>
        )}

        <section>
          {/* Gated on the SOURCE, never on status: a void invoice with no
              snapshot serves live (empty) lines, and the label over an empty
              table would be a new false claim. Says the lines were locked and
              nothing about QuickBooks (the copies can differ; QboPushPanel
              owns the QuickBooks sentences on this screen). */}
          {lines_source === 'frozen' && (
            <p data-testid="invoice-lines-frozen-label" className="mb-1 text-xs text-ink-soft italic">
              These lines were locked when the invoice was issued.
            </p>
          )}
          <table data-testid="invoice-lines" className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b border-sage-300">
                <th className="py-1 pr-2">Description</th>
                <th className="py-1 px-2 text-right">Qty (hrs)</th>
                <th className="py-1 px-2 text-right">Rate</th>
                <th className="py-1 pl-2 text-right">Amount</th>
                {isDraft && <th className="py-1 pl-2" />}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id} className="border-b border-sage-800/[8%]">
                  <td className="py-1 pr-2">
                    {/* The stored line with a first capital and no dictionary;
                        a line with no description passes through. */}
                    {/* On a household invoice, whose care it was, then the
                        text: the one composition QuickBooks receives too. */}
                    {l.service_label != null && l.service_label !== '' && (
                      <span data-testid="invoice-line-service-label" className="font-semibold text-ink">
                        {`${l.service_label}: `}
                      </span>
                    )}
                    {typeof l.description === 'string' ? displayValue(l.description) : l.description}
                    {/* The server's flag decides the Chip, never the words. */}
                    {l.after_hours && (
                      <Chip variant="caution" className="ml-2" data-testid={`invoice-line-after-hours-${l.id}`}>
                        After Hours
                      </Chip>
                    )}
                  </td>
                  <td className="py-1 px-2 text-right">{l.quantity}</td>
                  <td className="py-1 px-2 text-right">${l.unit_rate}</td>
                  <td className="py-1 pl-2 text-right">${l.amount}</td>
                  {isDraft && (
                    <td className="py-1 pl-2 text-right">
                      <Link
                        data-testid={`invoice-line-edit-${l.id}`}
                        className="text-xs text-sage-700 underline whitespace-nowrap"
                        to={preBillingHref}
                      >
                        Edit at source
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-sage-300 font-semibold">
                <td className="py-1 pr-2" colSpan={3}>Subtotal</td>
                <td data-testid="invoice-subtotal" className="py-1 pl-2 text-right">${invoice.subtotal}</td>
                {isDraft && <td />}
              </tr>
              <tr className="font-semibold">
                <td className="py-1 pr-2" colSpan={3}>Total</td>
                <td data-testid="invoice-total" className="py-1 pl-2 text-right">${invoice.total}</td>
                {isDraft && <td />}
              </tr>
            </tfoot>
          </table>
        </section>
      </article>

      {actionError && (
        <div data-testid="invoice-action-error" role="alert" className="text-sm text-pepper-deep">
          {actionError.message}
          {(actionError.code === 'missing_billing_contact' ||
            actionError.code === 'ambiguous_billing_contact') && (
            <>
              {' '}
              <Link
                data-testid="invoice-error-billing-details-link"
                className="underline"
                to={preBillingHref}
              >
                Set billing details on the Pre-billing tab
              </Link>
            </>
          )}
        </div>
      )}

      {/* QuickBooks (slice 6b): the invoice's standing push state, the
          per-invoice retry, and the ruled #90 warning when this invoice
          is voided here but its QuickBooks copy is still open. The panel
          renders nothing for a draft.

          LAST BLOCK ON THE PAGE, deliberately: the integration reports on
          a document it does not own, so it follows the document and the
          record's own action row, and its sage-tinted panel reads as the
          subordinate surface next to the invoice's paper card. Its
          internals (copy, logic, its own Send button) belong to the truth
          train and are not touched here. */}
      <QboPushPanel invoiceId={invoice.id} invoiceStatus={invoice.status} />
    </div>
  );
}
